import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge, Btn, Select } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { JOB_STATUSES, findMeta } from "@/lib/constants";
import { ExternalLink, RefreshCw, Truck, ScanLine } from "lucide-react";

const READY = ["qa_review", "ready_for_dispatch", "testing"];

export function ScanResult({ result, onClose, onRescan }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const job = result.kind === "job" ? result.job : result.jobs[0];
  const [status, setStatus] = useState(job.status);
  const [busy, setBusy] = useState(false);
  const st = findMeta(JOB_STATUSES, job.status);
  const go = (to) => { onClose(); navigate(to); };

  const saveStatus = async () => {
    setBusy(true);
    try {
      const ids = result.kind === "batch" ? result.jobs.filter((j) => j.status !== "dispatched").map((j) => j.id) : [job.id];
      for (const id of ids) await api.put(`/jobs/${id}`, { status });
      toast.success(`${ids.length > 1 ? `${ids.length} units` : job.job_number} → ${findMeta(JOB_STATUSES, status).label}`);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="scan-result">
      <div className="rounded-lg border border-border bg-accent/40 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{result.kind === "batch" ? `Batch ${result.batch_number} · ${result.jobs.length} units` : job.job_number}</div>
            <div className="truncate text-lg font-bold" data-testid="scan-result-title">{job.equipment_name}</div>
            {result.kind === "job" && <div className="font-mono text-xs text-muted-foreground">SN {job.serial_number}</div>}
          </div>
          <Badge className={st.cls}>{st.label}</Badge>
        </div>
      </div>

      <Btn size="lg" className="w-full" onClick={() => go(result.kind === "job" ? `/jobs/${job.id}` : `/inward?q=${encodeURIComponent(result.batch_number)}`)} data-testid="scan-open-job">
        <ExternalLink size={16} /> {result.kind === "job" ? "Open job" : "Open batch jobs"}
      </Btn>

      {can("job.edit") && job.status !== "dispatched" && (
        <div className="flex gap-2">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 flex-1" data-testid="scan-status-select">
            {JOB_STATUSES.filter((s) => s.value !== "dispatched").map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <Btn size="lg" variant="outline" disabled={busy || status === job.status} onClick={saveStatus} data-testid="scan-status-save">
            <RefreshCw size={16} /> Update
          </Btn>
        </div>
      )}

      {can("job.dispatch") && result.kind === "job" && READY.includes(job.status) && (
        <Btn size="lg" variant="outline" className="w-full" onClick={() => go(`/outward?dispatch=${job.id}`)} data-testid="scan-dispatch">
          <Truck size={16} /> Dispatch now
        </Btn>
      )}

      <button type="button" onClick={onRescan} className="flex w-full items-center justify-center gap-2 py-2 text-sm font-semibold text-primary" data-testid="scan-again">
        <ScanLine size={16} /> Scan another
      </button>
    </div>
  );
}
